import React, { useEffect, useState } from "react";
import { Card, Table, DatePicker, Select, Row, Col, Spin, Typography } from "antd";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, LineChart, Line } from "recharts";
import { useAuth0 } from "@auth0/auth0-react";
import dayjs from "dayjs";

// Declare global constants injected by Vite define
declare const API_BASE_URL: string;

const { RangePicker } = DatePicker;
const { Title } = Typography;

interface UserActivity {
  id: string;
  email: string;
  activity_type: string;
  activity_data: any;
  created_at: string;
}

const COLORS = ["#8884d8", "#82ca9d", "#ffc658", "#ff8042", "#8dd1e1", "#a4de6c", "#d0ed57", "#ffc0cb"];

// Helper function to generate date range with zero values
const generateDateRange = (startDate: dayjs.Dayjs, endDate: dayjs.Dayjs): { date: string; count: number }[] => {
  const dates: { date: string; count: number }[] = [];
  let current = startDate.startOf('day');
  
  while (current.isBefore(endDate) || current.isSame(endDate, 'day')) {
    dates.push({
      date: current.format('YYYY-MM-DD'),
      count: 0
    });
    current = current.add(1, 'day');
  }
  
  return dates;
};

// Helper function to format email for display
const formatEmailForDisplay = (email: string, maxLength: number = 20): string => {
  if (email.length <= maxLength) return email;
  const parts = email.split('@');
  if (parts[0].length > maxLength - 3) {
    return parts[0].substring(0, maxLength - 3) + '...';
  }
  return email.substring(0, maxLength) + '...';
};

// Custom tooltip for charts with comma formatting
const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div style={{ 
        backgroundColor: 'white', 
        padding: '8px', 
        border: '1px solid #ccc', 
        borderRadius: '4px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <p style={{ margin: 0, fontWeight: 'bold' }}>{`${label}`}</p>
        <p style={{ margin: 0, color: payload[0].color }}>
          {`Count: ${payload[0].value.toLocaleString()}`}
        </p>
      </div>
    );
  }
  return null;
};

export const UserActivityReport: React.FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [data, setData] = useState<UserActivity[]>([]);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState<[any, any] | null>([
    dayjs().subtract(29, 'days'),
    dayjs()
  ]);
  const [selectedActivityTypes, setSelectedActivityTypes] = useState<string[]>(["identify_conditions_with_codes"]);
  const [activityTypes, setActivityTypes] = useState<string[]>([]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [dateRange, selectedActivityTypes]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const params = new URLSearchParams();
      if (dateRange && dateRange[0] && dateRange[1]) {
        params.append("start_date", dateRange[0].toISOString());
        params.append("end_date", dateRange[1].toISOString());
      }
      if (selectedActivityTypes && selectedActivityTypes.length > 0) {
        selectedActivityTypes.forEach(type => {
          params.append("activity_type", type);
        });
      }
      const response = await fetch(`${API_BASE_URL}/api/admin/user-activity?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await response.json();
      
      setData(result);
      
      // Get all activity types from the unfiltered data for the dropdown
      const allTypesParams = new URLSearchParams();
      if (dateRange && dateRange[0] && dateRange[1]) {
        allTypesParams.append("start_date", dateRange[0].toISOString());
        allTypesParams.append("end_date", dateRange[1].toISOString());
      }
      const allTypesResponse = await fetch(`${API_BASE_URL}/api/admin/user-activity?${allTypesParams.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const allTypesResult = await allTypesResponse.json();
      setActivityTypes(Array.from(new Set(allTypesResult.map((a: UserActivity) => a.activity_type))));
    } catch (e) {
      // handle error
    } finally {
      setLoading(false);
    }
  };

  // Filter data to only show selected activity types for display
  const filteredData = selectedActivityTypes.length > 0 
    ? data.filter(activity => selectedActivityTypes.includes(activity.activity_type))
    : data;

  // Chart: Activity over time (by day) - with separate lines for each activity type
  const getActivityByDayData = () => {
    if (filteredData.length === 0) return { chartData: [], activityTypesList: [] };

    // Get actual date range from data or use filter dates
    const dates = filteredData.map(d => dayjs(d.created_at));
    const minDate = dateRange?.[0] ? dayjs(dateRange[0]) : dates.reduce((min, current) => current.isBefore(min) ? current : min, dates[0]);
    const maxDate = dateRange?.[1] ? dayjs(dateRange[1]) : dates.reduce((max, current) => current.isAfter(max) ? current : max, dates[0]);

    // Generate complete date range with zeros
    const dateRangeData = generateDateRange(minDate, maxDate);

    // Get unique activity types from filtered data
    const uniqueActivityTypes = Array.from(new Set(filteredData.map(d => d.activity_type)));

    // Build activity counts by day and type
    const activityByDayAndType: Record<string, Record<string, number>> = {};
    
    filteredData.forEach(activity => {
      const day = dayjs(activity.created_at).format("YYYY-MM-DD");
      if (!activityByDayAndType[day]) {
        activityByDayAndType[day] = {};
      }
      activityByDayAndType[day][activity.activity_type] = (activityByDayAndType[day][activity.activity_type] || 0) + 1;
    });

    // Create chart data with all activity types for each day
    const chartData = dateRangeData.map(item => {
      const dayData: any = {
        date: item.date,
        displayDate: dayjs(item.date).format('MMM DD')
      };
      
      // Add each activity type as a property
      uniqueActivityTypes.forEach(activityType => {
        dayData[activityType] = activityByDayAndType[item.date]?.[activityType] || 0;
      });
      
      return dayData;
    }).sort((a, b) => a.date.localeCompare(b.date));

    return { chartData, activityTypesList: uniqueActivityTypes };
  };

  const { chartData: activityByDayData, activityTypesList } = getActivityByDayData();

  // Chart: Activity type breakdown
  const activityTypeCounts = filteredData.reduce((acc, curr) => {
    acc[curr.activity_type] = (acc[curr.activity_type] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  const activityTypeData = Object.entries(activityTypeCounts).map(([type, count]) => ({ 
    type: type.replace(/_/g, ' ').toUpperCase(), 
    count,
    originalType: type
  }));

  // Chart: Top users by activity - improved formatting
  const userCounts = filteredData.reduce((acc, curr) => {
    acc[curr.email] = (acc[curr.email] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  const topUsers = Object.entries(userCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10)
    .map(([email, count]) => ({ 
      email, 
      count,
      displayEmail: formatEmailForDisplay(email, 25)
    }));

  const columns = [
    { title: "Date", dataIndex: "created_at", key: "created_at", render: (v: string) => dayjs(v).format("YYYY-MM-DD HH:mm") },
    { title: "Email", dataIndex: "email", key: "email" },
    { title: "Type", dataIndex: "activity_type", key: "activity_type" },
    { title: "Details", dataIndex: "activity_data", key: "activity_data", render: (v: any) => <pre style={{ maxWidth: 300, whiteSpace: 'pre-wrap' }}>{JSON.stringify(v, null, 2)}</pre> },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Title level={2}>User Activity Report</Title>
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={16} align="middle">
          <Col>
            <RangePicker
              value={dateRange}
              onChange={setDateRange}
              allowClear
              placeholder={['Start date', 'End date']}
            />
          </Col>
          <Col>
            <Select
              mode="multiple"
              placeholder="Select Activity Types"
              value={selectedActivityTypes}
              onChange={setSelectedActivityTypes}
              style={{ minWidth: 300 }}
              maxTagCount="responsive"
              options={activityTypes.map(type => ({ value: type, label: type.replace(/_/g, ' ').toUpperCase() }))}
              disabled={activityTypes.length === 0}
              loading={loading && activityTypes.length === 0}
            />
          </Col>
        </Row>
      </Card>
      {loading ? (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
        </div>
      ) : (
        <>
          {/* Activity Over Time - Full width for better readability */}
          <Card title="Activity Over Time" style={{ marginBottom: 24 }}>
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={activityByDayData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                <XAxis 
                  dataKey="displayDate" 
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  interval={Math.max(1, Math.floor(activityByDayData.length / 15))}
                  style={{ fontSize: '12px' }}
                />
                <YAxis 
                  allowDecimals={false}
                  tickFormatter={(value) => value.toLocaleString()}
                />
                <Tooltip 
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      return (
                        <div style={{ 
                          backgroundColor: 'white', 
                          padding: '8px', 
                          border: '1px solid #ccc', 
                          borderRadius: '4px',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}>
                          <p style={{ margin: 0, fontWeight: 'bold' }}>{`${label}`}</p>
                          {payload.map((entry, index) => (
                            <p key={index} style={{ margin: 0, color: entry.color }}>
                              {`${entry.dataKey?.toString().replace(/_/g, ' ').toUpperCase()}: ${entry.value?.toLocaleString()}`}
                            </p>
                          ))}
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Legend />
                {activityTypesList.map((activityType, index) => (
                  <Line 
                    key={activityType}
                    type="monotone" 
                    dataKey={activityType} 
                    stroke={COLORS[index % COLORS.length]}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    activeDot={{ r: 5 }}
                    name={activityType.replace(/_/g, ' ').toUpperCase()}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </Card>

          <Row gutter={24} style={{ marginBottom: 24 }}>
            {/* Activity Type Breakdown */}
            <Col span={12}>
              <Card title="Activity Type Breakdown">
                <ResponsiveContainer width="100%" height={350}>
                  <PieChart>
                    <Pie
                      data={activityTypeData}
                      dataKey="count"
                      nameKey="type"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      label={({ type, count, percent }) => 
                        `${type}: ${count.toLocaleString()} (${(percent * 100).toFixed(1)}%)`
                      }
                      labelLine={false}
                    >
                      {activityTypeData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value: any) => [value.toLocaleString(), 'Count']}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </Card>
            </Col>

            {/* Top Users - Vertical bar chart for better readability */}
            <Col span={12}>
              <Card title="Top Users">
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart 
                    data={topUsers} 
                    margin={{ top: 20, right: 30, left: 20, bottom: 100 }}
                  >
                    <XAxis 
                      dataKey="displayEmail" 
                      angle={-45}
                      textAnchor="end"
                      height={120}
                      interval={0}
                      style={{ fontSize: '11px' }}
                    />
                    <YAxis 
                      allowDecimals={false}
                      tickFormatter={(value) => value.toLocaleString()}
                    />
                    <Tooltip 
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload;
                          return (
                            <div style={{ 
                              backgroundColor: 'white', 
                              padding: '8px', 
                              border: '1px solid #ccc', 
                              borderRadius: '4px',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}>
                              <p style={{ margin: 0, fontWeight: 'bold' }}>{data.email}</p>
                              <p style={{ margin: 0, color: payload[0].color }}>
                                {`Count: ${data.count.toLocaleString()}`}
                              </p>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Bar 
                      dataKey="count" 
                      fill="#82ca9d"
                      label={{ 
                        position: 'top', 
                        formatter: (value: any) => value.toLocaleString(),
                        style: { fontSize: '12px' }
                      }}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            </Col>
          </Row>

          <Card title="All User Activity">
            <Table
              dataSource={filteredData}
              columns={columns}
              rowKey="id"
              pagination={{ 
                pageSize: 20,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => 
                  `${range[0]}-${range[1]} of ${total.toLocaleString()} items`
              }}
              scroll={{ x: true }}
            />
          </Card>
        </>
      )}
    </div>
  );
};

export async function logLoginActivity(getAccessTokenSilently: () => Promise<string>) {
  const token = await getAccessTokenSilently();
  await fetch(`${API_BASE_URL}/api/user/log-login`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
  });
} 